import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { LoadingState, Section } from '../components/public/ui';
import { useMenuVisibility } from '../contexts/MenuVisibilityContext';

const Home = () => {
  const { ready, menuItems, visibleSlugs } = useMenuVisibility();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const response = await api.get('/public/home');
        setPageData(response.data.data);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPageData();
  }, []);

  const features = useMemo(
    () =>
      menuItems
        .filter((item) => item.show_on_home && visibleSlugs.has(item.slug))
        .map((item) => ({
          slug: item.slug,
          title: item.label,
          description: item.card_description || '',
          link: item.path,
        })),
    [menuItems, visibleSlugs],
  );

  const showContactCta = visibleSlugs.has('contact');

  if (loading || !ready) {
    return <LoadingState />;
  }

  return (
    <>
      <section className="relative overflow-hidden bg-primary text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.25), transparent 45%), radial-gradient(circle at 80% 0%, rgba(0,0,0,0.25), transparent 40%)',
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-content px-4 py-10 md:px-6 md:py-14 lg:px-8">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-white/80">
            Kickboxing
          </p>
          <h1 className="mt-2 max-w-3xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-wide md:text-6xl lg:text-7xl">
            {pageData?.title || 'Team Kitsune'}
          </h1>
          <p className="mt-4 max-w-xl text-base text-white/90 md:text-lg">
            {pageData?.content ||
              'Escuela de Kickboxing dedicada a la formación integral'}
          </p>
          {showContactCta && (
            <div className="mt-6">
              <Link
                to="/contact"
                className="inline-flex min-h-12 cursor-pointer items-center justify-center rounded-md bg-white px-8 py-3 font-semibold text-primary transition-colors duration-200 hover:bg-canvas"
              >
                Contáctanos
              </Link>
            </div>
          )}
        </div>
      </section>

      {features.length > 0 && (
        <Section className="section-pad">
          <h2 className="font-display text-3xl font-bold uppercase tracking-wide md:text-4xl">
            Explorar
          </h2>
          <p className="mt-2 max-w-xl text-muted">
            Accesos directos a lo esencial del dojo.
          </p>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {features.map((feature) => (
              <li key={feature.slug}>
                <Link
                  to={feature.link}
                  className="group flex h-full min-h-[10rem] flex-col justify-between rounded-lg border border-line bg-surface p-6 transition-colors duration-200 hover:border-primary"
                >
                  <div>
                    <h3 className="font-display text-2xl font-semibold uppercase tracking-wide text-ink group-hover:text-primary">
                      {feature.title}
                    </h3>
                    {feature.description && (
                      <p className="mt-2 text-sm text-muted">{feature.description}</p>
                    )}
                  </div>
                  <span className="mt-6 text-sm font-semibold text-primary">Ver más →</span>
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </>
  );
};

export default Home;
